import type { BaseFieldStore } from '@/Core/UI/Form/Stores/BaseFieldStore'
import type { FieldType } from './Form'

/**
 * Інтерфейс стратегії для обробки специфічної логіки типу поля
 * Кожен тип поля може мати свою стратегію обробки значень
 */
export interface IFieldInputStrategy {
    /**
     * Тип поля, для якого працює ця стратегія
     */
    fieldType: FieldType

    /**
     * Чи підтримує поле власну множинність (наприклад, Select з mode="multiple")
     * Якщо true, то FormMultipleField НЕ використовується для isMultiple
     */
    supportsNativeMultiple?: boolean

    /**
     * Назва властивості для прив'язки значення (value, checked тощо)
     */
    getValueName?(field: BaseFieldStore): string

    /**
     * Витягує значення з події onChange
     * @param event - подія від input компонента
     * @param field - поле форми
     */
    extractValue?(event: any, field: BaseFieldStore): any

    /**
     * Трансформує значення для режиму редагування (input)
     * @param value - поточне значення
     * @param field - поле форми
     */
    transformForEdit?(value: any, field: BaseFieldStore): any

    /**
     * Трансформує значення для режиму перегляду (view)
     * @param value - поточне значення
     * @param field - поле форми
     */
    transformForView?(value: any, field: BaseFieldStore): any

    /**
     * Додаткова валідація специфічна для типу
     * @param value - значення для валідації
     * @param field - поле форми
     */
    validate?(value: any, field: BaseFieldStore): string | null

    /**
     * Модифікує props перед передачею в input компонент
     * @param props - базові props
     * @param field - поле форми
     */
    modifyInputProps?(props: Record<string, any>, field: BaseFieldStore): Record<string, any>
}

/**
 * Реєстр стратегій для різних типів полів
 */
export class FieldInputStrategyRegistry {
    private static strategies = new Map<FieldType, IFieldInputStrategy>()

    /**
     * Реєструє стратегію для типу поля
     */
    static register(strategy: IFieldInputStrategy): void {
        this.strategies.set(strategy.fieldType, strategy)
    }

    /**
     * Отримує стратегію для типу поля
     */
    static get(fieldType: FieldType): IFieldInputStrategy | undefined {
        return this.strategies.get(fieldType)
    }

    /**
     * Перевіряє, чи є стратегія для типу поля
     */
    static has(fieldType: FieldType): boolean {
        return this.strategies.has(fieldType)
    }

    /**
     * Видаляє всі стратегії (для тестування)
     */
    static clear(): void {
        this.strategies.clear()
    }
}
